import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import useTip from 'hooks/useTip.hook';
import { useTheme } from 'providers/Theme.provider';
import { nowAsDate } from 'utils/date/now.util';
import call from 'utils/functions/call.util';

import CN from './MonthPicker.module.scss';

const MONTHS = Object.freeze(['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']);
const DISPLAY = Object.freeze({
  jan: 'January',
  feb: 'February',
  mar: 'March',
  apr: 'April',
  may: 'May',
  jun: 'June',
  jul: 'July',
  aug: 'August',
  sep: 'September',
  oct: 'October',
  nov: 'November',
  dec: 'December',
});

// TODO: move to another place in src/utils
// TODO: check the input
const fromMonthToAbbreviatedString = (month) => month.slice(0, 3);

// TODO: move to another place in src/utils
const fromMonthToFullString = (month) => DISPLAY[month] || 'Unknown';

// TODO: move to another place in src/utils
const fromMonthAndYearToDisplay = (options) => `${fromMonthToFullString(options?.month)}, ${options?.year}`;

const MonthPicker = ({ withOverlay, onChange, onClose, autoclose }) => {
  const { isDarkMode } = useTheme();

  // get current month and year
  const currentDate = nowAsDate();
  const currentMonth = MONTHS[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  const { val: isOpen, tip: toggleDialog, nay: closeDialog } = useTip(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const handlePrevYear = useCallback(() => void setSelectedYear(selectedYear - 1), [selectedYear]);
  const handleNextYear = useCallback(() => void setSelectedYear(selectedYear + 1), [selectedYear]);

  const handleCancel = useCallback(() => {
    if (autoclose) closeDialog();
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear); // TODO: reset selected year month to default or other more complex logic
  }, [autoclose, closeDialog, currentMonth, currentYear]);

  const handleView = useCallback(() => {
    if (autoclose) closeDialog();
    call('MonthPicker.onClose', onClose, { month: selectedMonth, year: selectedYear });
  }, [autoclose, closeDialog, onClose, selectedMonth, selectedYear]);

  useEffect(() => {
    call('MonthPicker.onChange', onChange, { month: selectedMonth, year: selectedYear });
  }, [onChange, selectedMonth, selectedYear]);

  return (
    <div
      data-bem="MonthPicker"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <input
        type="text"
        placeholder="select month"
        value={fromMonthAndYearToDisplay({ month: selectedMonth, year: selectedYear })}
        onClick={toggleDialog}
        readOnly
        className={CN.pickerInput}
      />
      {isOpen && (
        <>
          {withOverlay && <div className={CN.overlay}></div>}
          <div className={CN.pickerPanel}>
            <div className={CN.pickerBody}>
              <div className={CN.pickerHeader}>
                <Button variant="empty" onClick={handlePrevYear}>
                  <Icon variant="arrowBack" />
                </Button>
                <span className={CN.pickerYear}>{selectedYear}</span>
                <Button variant="empty" onClick={handleNextYear}>
                  <Icon variant="arrowBack" />
                </Button>
              </div>
              <div className={CN.pickerTitle}>Choose a month to view/edit:</div>
              <div className={CN.pickerButtons}>
                {MONTHS.map((month, index) => (
                  <div
                    className={cn(CN.pickerButton, {
                      [CN.selected]: month === selectedMonth,
                    })}
                    key={index}
                    onClick={() => setSelectedMonth(month)}
                  >
                    {fromMonthToAbbreviatedString(month)}
                  </div>
                ))}
              </div>
            </div>
            <div className={CN.pickerFooter}>
              <Button variant="tertiary" onClick={handleCancel}>
                CANCEL
              </Button>
              <Button variant="primary" onClick={handleView}>
                VIEW
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthPicker;
